import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SupportButton from "@/components/base/SupportButton";
import { FileText, FileCheck, HelpCircle, DollarSign } from "lucide-react";

export default function TermsPage() {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className={`min-h-screen ${darkMode ? "bg-slate-950 text-white" : "bg-white text-slate-900"}`}>
        <Navbar darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} />

        <main className="pt-24 pb-16 px-4 md:px-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-full px-4 py-1.5 mb-4">
              <FileText size={14} className="text-blue-700 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-800 dark:text-blue-300">Términos Legales del Servicio</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-3">Términos del Servicio</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Última actualización: 29 de mayo de 2026</p>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none text-slate-600 dark:text-slate-300 space-y-8">
            <section className="bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/40 rounded-2xl p-6">
              <p className="text-sm leading-relaxed mb-0">
                Al registrar tu empresa en la plataforma **emite.do**, aceptas cumplir con los siguientes Términos del Servicio. Estos términos regulan el uso de nuestra API de facturación electrónica e-CF, el portal transaccional cliente y los servicios complementarios.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <DollarSign className="text-blue-800 dark:text-blue-400" size={18} />
                1. Políticas de Facturación y Costos
              </h2>
              <p className="text-sm leading-relaxed">
                El servicio se rige por un esquema de cobro transaccional de pago por uso con un cargo base mínimo:
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Corte Mensual:</strong> emite.do realiza el corte de emisiones de comprobantes fiscales electrónicos acumulados los días <strong>1 de cada mes a las 00:00 horas (Zona Horaria Santo Domingo)</strong>, procediendo a emitir la factura correspondiente.</li>
                <li><strong>Cargo Mínimo Operativo:</strong> Se establece una facturación base mínima de <strong>USD$ 25.00 mensuales</strong> por RNC activo en producción. Si el consumo transaccional de e-CF aceptados durante el periodo mensual es menor o igual a esta cantidad, se cobrará la tarifa mínima de USD$ 25.00. Si el volumen es mayor, se cobrará el consumo real acumulado según la escala decreciente.</li>
                <li><strong>Habilitación en Producción:</strong> Los cargos transaccionales y el mínimo operativo comienzan a computarse exclusivamente a partir del momento en que el RNC del cliente es habilitado por la DGII en el <strong>Ambiente de Producción (eCF)</strong>.</li>
                <li><strong>Ambiente de Pruebas Sin Costo:</strong> El acceso a los ambientes de pruebas y simulación (`TesteCF` y `CerteCF`) para el desarrollo, pruebas e integraciones y durante todo el proceso de certificación técnica de emisor <strong>no tiene ningún costo mensual</strong>.</li>
                <li><strong>Soporte de Certificación Incluido:</strong> Todo el acompañamiento técnico, simulación y set de pruebas requeridos para certificar al cliente ante la DGII está incluido en el servicio básico.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <FileCheck className="text-blue-800 dark:text-blue-400" size={18} />
                2. Responsabilidad de Emisión y Firmas
              </h2>
              <p className="text-sm leading-relaxed">
                El cliente es el único responsable legal del contenido de los payloads JSON transmitidos a la API de emite.do.
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Validez de la Firma:</strong> Al cargar el certificado digital `.p12` y proporcionar el PIN, autorizas a emite.do a realizar la firma digital inmutable del XML fiscal en representación de tu empresa.</li>
                <li><strong>Rangos NCF Autorizados:</strong> El cliente debe solicitar y registrar previamente en su cuenta los rangos de e-NCF autorizados por la DGII. emite.do no es responsable por comprobantes no emitidos debido a la falta de secuencias disponibles o expiradas.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <HelpCircle className="text-blue-800 dark:text-blue-400" size={18} />
                3. Disponibilidad y Límite de Responsabilidad (SLA)
              </h2>
              <p className="text-sm leading-relaxed">
                emite.do garantiza un nivel de servicio (SLA) del 99.9% de disponibilidad en su API Gateway y sistemas de procesamiento interno.
              </p>
              <p className="text-sm leading-relaxed">
                <strong>Exclusión de Responsabilidad por Fallos de la DGII:</strong> El proceso final de validación y aceptación del comprobante fiscal depende de los servidores de la DGII de la República Dominicana. emite.do no se hace responsable por retrasos, rechazos tardíos o caídas del servicio causados directamente por indisponibilidad, mantenimiento o fallos en la infraestructura de la DGII. Ante caídas de la DGII, nuestra plataforma activará automáticamente las colas de contingencia y reintentos automáticos configurados legalmente.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                4. Modificación de los Términos
              </h2>
              <p className="text-sm leading-relaxed">
                emite.do se reserva el derecho de modificar estos términos previo aviso por correo electrónico a las cuentas autorizadas con al menos 30 días de anticipación antes de aplicar cualquier cambio comercial o de condiciones.
              </p>
            </section>
          </div>
        </main>

        <Footer darkMode={darkMode} />
        <SupportButton />
      </div>
    </div>
  );
}
